import { NestedDownloadData } from "@nova/component-export-download-buttons-hover"
import * as Icons from "@nova/component-icons"
import { InspectorPanelAccordionExportFileOptions } from "./InspectorPanelAccordionExportFileOptions"
import { ButtonPrimary, ButtonStatus } from "@nova/component-btn"
import { useEffect, useMemo, useState } from "react"
import { get } from "nodash"
import { InspectorPanelAccordion } from "../InspectorPanelAccordion"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectCopyingExportId } from "../../slice"
import { selectIsDomainWhiteListed, selectUserLicense } from "user/profile/selectors"
import { usePaywalls } from "features/entity-caching/licenses/useLicensePaywall"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { useWindowHeight } from "tools/hooks/useWindowHeight"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"

export const InspectorPanelAccordionExport = ({
    exportOptions,
    canOnlyPickOne,
    isDownloading,
    isDefaultCollapsed,
}: {
    exportOptions: NestedDownloadData
    canOnlyPickOne: boolean
    isDownloading?: boolean
    isDefaultCollapsed?: boolean
}) => {
    const dispatch = useAppDispatch()
    const exportOptionIdBeingCopied = useAppSelector(selectCopyingExportId)
    const userLicense = useAppSelector(selectUserLicense)
    const isWhitelisted = useAppSelector(selectIsDomainWhiteListed)
    const { showLicensePaywallModal } = usePaywalls()
    const windowHeight = useWindowHeight()

    const [selectedExportOptions, setSelectedExportOptions] = useState(new Set<string>())
    const [closedCategories, setClosedCategories] = useState(new Set<string>())
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        if (!isDefaultCollapsed) return
        const categories = Object.keys(exportOptions)
        setClosedCategories(new Set(categories))
    }, [exportOptions, isDefaultCollapsed])

    const addExportOption = (option: string) => {
        if (!canOnlyPickOne) {
            selectedExportOptions.add(option)
            setSelectedExportOptions(new Set(Array.from(selectedExportOptions)))
        } else {
            setSelectedExportOptions(new Set([option]))
        }
    }

    const removeExportOption = (option: string) => {
        if (!canOnlyPickOne) {
            selectedExportOptions.delete(option)
            setSelectedExportOptions(new Set(Array.from(selectedExportOptions)))
        } else {
            setSelectedExportOptions(new Set())
        }
    }

    const setCategoryIsClosed = (category: string, isClosed: boolean) => {
        if (isClosed) {
            closedCategories.add(category)

            // De-select options in this category if it closes.
            selectedExportOptions.forEach((option) => {
                if (option.includes(category)) selectedExportOptions.delete(option)
            })
            setSelectedExportOptions(new Set(Array.from(selectedExportOptions)))
        } else closedCategories.delete(category)
        setClosedCategories(new Set(Array.from(closedCategories)))
    }

    const downloadSelection = async () => {
        setIsDownloadingLocal(true)
        try {
            const promises = Array.from(selectedExportOptions).map((option) => {
                const split = option.split("-")
                const path = split.length === 3 ? [`${split[0]}-${split[1]}`, split[2]] : split
                const { callback, restrictedLicenses } = get(exportOptions, path) as {
                    callback?: unknown
                    restrictedLicenses?: string[]
                }
                if (
                    restrictedLicenses?.includes(userLicense?.subscriptionType || "") &&
                    !isWhitelisted
                ) {
                    showLicensePaywallModal("exports")
                    return Promise.resolve()
                }
                if (typeof callback === "function") return (callback as () => Promise<void>)()
                return Promise.resolve()
            })
            await Promise.all(promises)
        } finally {
            setIsDownloadingLocal(false)
            setSelectedExportOptions(new Set())
        }
    }

    const soloExportOption = useMemo(() => {
        const options = Object.entries(exportOptions)
        if (options.length !== 1) return

        const [title, exportOption] = options[0]

        if (typeof exportOption.callback === "function")
            return { callback: exportOption.callback, title }
    }, [exportOptions])

    const [soloDownloadButtonIsDisabled, setSoloDownloadButtonIsDisabled] = useState(false)
    const [isDownloadingLocal, setIsDownloadingLocal] = useState(false)

    return Object.keys(exportOptions).length > 0 ? (
        <InspectorPanelAccordion
            title="Export"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            shouldPadChildrenVertically={false}
            extraVerticalPaddingInRem={0.5}
        >
            {/* Export Options */}
            {exportOptions &&
                (!soloExportOption ? (
                    <>
                        <ScrollableArea style={{ maxHeight: `${windowHeight - 450}px` }}>
                            <InspectorPanelAccordionExportFileOptions
                                downloadOptions={exportOptions}
                                selectedExportOptions={selectedExportOptions}
                                addExportOption={addExportOption}
                                removeExportOption={removeExportOption}
                                closedCategories={closedCategories}
                                setCategoryIsClosed={setCategoryIsClosed}
                                exportIdBeingCopied={exportOptionIdBeingCopied}
                            />
                        </ScrollableArea>
                        <div
                            className="bg-gray-700 h-px"
                            style={{ width: "calc(100% + 1.25rem)", translate: "-0.625rem 0" }}
                        />
                        <ButtonPrimary
                            title="Download"
                            LeftIcon={Icons.Download}
                            disabled={selectedExportOptions.size === 0 || isDownloadingLocal}
                            onClick={downloadSelection}
                            status={
                                isDownloading || isDownloadingLocal
                                    ? ButtonStatus.Loading
                                    : undefined
                            }
                        />
                    </>
                ) : (
                    <ButtonPrimary
                        title={"Download " + soloExportOption.title}
                        LeftIcon={Icons.Download}
                        className="w-full mt-2"
                        status={
                            soloDownloadButtonIsDisabled
                                ? ButtonStatus.Loading
                                : ButtonStatus.Normal
                        }
                        disabled={soloDownloadButtonIsDisabled}
                        onClick={async () => {
                            setSoloDownloadButtonIsDisabled(true)
                            try {
                                await soloExportOption.callback()
                            } catch (err) {
                                console.error(err)
                                dispatch(
                                    setGlobalNotificationContext({
                                        message: "Failed to export the file. Please try again.",
                                        status: GlobalNotificationStatus.error,
                                        timeoutInMs: 5000,
                                    })
                                )
                            }
                            setSoloDownloadButtonIsDisabled(false)
                        }}
                    />
                ))}
        </InspectorPanelAccordion>
    ) : null
}
